import React from 'react';
import { formatDate, formatMoney } from '../../utils/constants';
import { styles } from './styles';

function PaymentTab({
  formData,
  setFormData,
  payments,
  newPayment,
  setNewPayment,
  isAddingPayment,
  handleAddPayment,
  handleDeletePayment,
  effectiveMaterialCost,
  laborCost,
  history = []
}) {
  const totalPaid = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  
  // 전체금액 = 부품비용 + 공임
  const totalCostWithMaterials = effectiveMaterialCost + parseFloat(laborCost || 0);
  const vatAmount = formData.vatIncluded ? Math.floor(totalCostWithMaterials * 0.1) : 0;
  const totalCostWithVAT = totalCostWithMaterials + vatAmount;

  // 미수금을 음수로 표시하기 위해 [받은 금액 - 청구 금액]으로 계산 순서 변경
  const outstanding = totalPaid - totalCostWithVAT;

  // 합계금액 변경 이력 필터링
  const amountHistory = history.filter(h => h.field_name === '합계금액');

  return (
    <div style={styles.fadeIn}>
      <div style={styles.formSection}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '8px', borderBottom: '2px solid #e8ecef' }}>
          <h4 style={{ ...styles.sectionTitle, marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>결제 요약</h4>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <span style={{ fontSize: '14px', color: '#7f8c8d', fontWeight: '600' }}>부가세</span>
            <div 
              onClick={() => setFormData(prev => ({ ...prev, vatIncluded: !prev.vatIncluded }))}
              style={{
                width: '44px',
                height: '24px',
                backgroundColor: formData.vatIncluded ? '#27ae60' : '#bdc3c7',
                borderRadius: '12px',
                position: 'relative',
                transition: 'background-color 0.2s',
                cursor: 'pointer'
              }}
            >
              <div style={{
                width: '20px',
                height: '20px',
                backgroundColor: 'white',
                borderRadius: '50%',
                position: 'absolute',
                top: '2px',
                left: formData.vatIncluded ? '22px' : '2px',
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }} />
            </div>
          </label>
        </div>
        <div style={styles.paymentSummary}>
          <div style={styles.summaryCard}>
            <span style={styles.summaryLabel}>전체금액</span>
            <span style={styles.summaryValue}>{formatMoney(totalCostWithMaterials)}</span>
          </div>
          <div style={styles.summaryCard}>
            <span style={styles.summaryLabel}>부가세 (10%)</span>
            <span style={{...styles.summaryValue, color: formData.vatIncluded ? '#3498db' : '#bdc3c7'}}>
              {formatMoney(vatAmount)}
            </span>
          </div>
          <div style={styles.summaryCard}>
            <span style={styles.summaryLabel}>합계금액</span>
            <span style={{...styles.summaryValue, color: '#2c3e50'}}>{formatMoney(totalCostWithVAT)}</span>
          </div>
          <div style={styles.summaryCard}>
            <span style={styles.summaryLabel}>미수금 / 선결제</span>
            <span style={{...styles.summaryValue, color: outstanding < 0 ? '#e74c3c' : '#27ae60'}}>
              {formatMoney(outstanding)}
            </span>
          </div>
        </div>

        {/* 금액 변경 이력 표시 영역 */}
        {amountHistory.length > 0 && (
          <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fff9db', borderRadius: '8px', border: '1px solid #ffe066' }}>
            <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#856404', marginBottom: '8px' }}>💰 합계금액 변경 이력</div>
            {amountHistory.map((h, i) => (
              <div key={i} style={{ fontSize: '12px', color: '#666', display: 'flex', gap: '8px', marginBottom: '4px' }}>
                <span>• {new Date(h.changed_at).toLocaleDateString()}</span>
                <span style={{ textDecoration: 'line-through', color: '#999' }}>{formatMoney(h.old_value)}</span>
                <span>→</span>
                <span style={{ fontWeight: 'bold', color: '#e67e22' }}>{formatMoney(h.new_value)}</span>
                <span style={{ color: '#999' }}>({h.username})</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={styles.formSection}>
        <h4 style={styles.sectionTitle}>결제 추가</h4>
        <div style={{...styles.formGrid, gridTemplateColumns: 'repeat(4, 1fr)'}}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>결제일</label>
            <input
              type="date"
              value={newPayment.paymentDate}
              onChange={(e) => setNewPayment(prev => ({...prev, paymentDate: e.target.value}))}
              style={styles.input}
            />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>결제방식</label>
            <select
              value={newPayment.paymentMethod}
              onChange={(e) => setNewPayment(prev => ({...prev, paymentMethod: e.target.value}))}
              style={styles.input}
            >
              <option value="">선택하세요</option>
              <option value="현금">현금</option>
              <option value="카드">카드</option>
              <option value="통장">통장</option>
            </select>
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>계산서</label>
            <select
              value={newPayment.taxInvoice}
              onChange={(e) => {
                const val = e.target.value;
                setNewPayment(prev => ({...prev, taxInvoice: val}));
                if (val) {
                  setFormData(prev => ({ ...prev, vatIncluded: true }));
                }
              }}
              style={styles.input}
            >
              <option value="">선택하세요</option>
              <option value="발행">발행</option>
              <option value="현금">현금</option>
              <option value="카드">카드</option>
            </select>
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>결제금액</label>
            <input
              type="text"
              value={formatMoney(newPayment.amount)}
              onChange={(e) => setNewPayment(prev => ({...prev, amount: e.target.value.replace(/[^\d]/g, '')}))}
              placeholder="금액을 입력하세요"
              style={styles.input}
            />
          </div>
        </div>
        <div style={{display: 'flex', justifyContent: 'center', marginTop: '20px'}}>
          <button 
            onClick={handleAddPayment} 
            style={{
              ...styles.addButton,
              ...(isAddingPayment ? styles.addButtonDisabled : {}),
              marginTop: 0,
              minWidth: '200px'
            }} 
            disabled={isAddingPayment}
          >
            {isAddingPayment ? '추가 중...' : '결제 추가'}
          </button>
        </div>
      </div>

      <div style={styles.formSection}>
        <h4 style={styles.sectionTitle}>결제 내역</h4>
        {payments.length === 0 ? (
          <div style={styles.emptyState}>
            <span style={styles.emptyIcon}>💳</span>
            <p>결제 내역이 없습니다</p>
          </div>
        ) : (
          <div style={styles.paymentList}>
            {payments.map(payment => (
              <div key={payment.id} style={styles.paymentItem}>
                <div style={styles.paymentInfo}>
                  <div style={styles.paymentDate}>{formatDate(payment.payment_date)}</div>
                  <div style={styles.paymentDetails}>
                    <span style={styles.paymentAmount}>{formatMoney(payment.amount)}</span>
                    <span style={styles.paymentMethod}>({payment.payment_method})</span>
                    {payment.tax_invoice && (
                      <span style={styles.paymentMethod}>[{payment.tax_invoice}]</span>
                    )}
                  </div>
                </div>
                <button 
                  onClick={() => handleDeletePayment(payment.id)}
                  style={styles.deleteButton}
                >
                  삭제
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default PaymentTab;
